import { apiRequest, ApiError, queryClient } from '@/lib/queryClient';

const SYNC_QUEUE_KEY = 'doublepress_sync_queue';
const FAILED_QUEUE_KEY = 'doublepress_failed_syncs';

export type SyncAction =
  | { type: 'hole_scores'; roundId: string; holeNumber: number; scores: Array<{ participantId: string; grossScore: number; netScore: number; receivesStroke: boolean }> }
  | { type: 'press'; roundId: string; betType: string; initiatedBy: 'top' | 'bottom'; startingHole: number; stackCount: number; parentPressId?: string; clientId?: string }
  | { type: 'clear_holes'; roundId: string; holeNumbers: number[] }
  | { type: 'delete_press'; roundId: string; pressId: string }
  | { type: 'active_hole'; roundId: string; holeNumber: number };

interface QueueEntry {
  id: string;
  action: SyncAction;
  retries: number;
  createdAt: number;
  /** The internal user.id of the commissioner who created this entry.
   *  undefined on entries written before this field was added (legacy). */
  commissionerUserId?: string;
}

let listeners: Array<(count: number) => void> = [];

function notifyListeners(count: number) {
  listeners.forEach(fn => fn(count));
}

export function subscribeSyncStatus(fn: (count: number) => void): () => void {
  listeners.push(fn);
  fn(loadQueue().length);
  return () => { listeners = listeners.filter(l => l !== fn); };
}

// ── Failed sync store ────────────────────────────────────────────────────────
// hole_scores AND press entries that exhaust MAX_RETRIES are moved here instead
// of being silently discarded — both are money/state-affecting and must be
// visible + retryable. Other action types (active_hole, clear_holes, delete_press)
// are still silently dropped — they are non-critical or self-healing on reload.

let failedListeners: Array<() => void> = [];

function notifyFailedListeners() {
  failedListeners.forEach(fn => fn());
}

function loadFailed(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(FAILED_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveFailed(queue: QueueEntry[]): void {
  try {
    localStorage.setItem(FAILED_QUEUE_KEY, JSON.stringify(queue));
    notifyFailedListeners();
  } catch (e) {
    console.error('[syncQueue] Failed to save failed queue:', e);
  }
}

export type FailedHoleInfo = { holeNumber: number };
export type FailedPressInfo = { clientId: string; betType: string; startingHole: number };
export type FailedDeleteInfo = { pressId: string };

export function subscribeFailedSyncs(
  roundId: string,
  fn: (holes: number[]) => void,
): () => void {
  const notify = () => {
    const holes = loadFailed()
      .filter(e => e.action.type === 'hole_scores' && e.action.roundId === roundId)
      .map(e => (e.action as Extract<SyncAction, { type: 'hole_scores' }>).holeNumber);
    fn(holes);
  };
  failedListeners.push(notify);
  notify();
  return () => { failedListeners = failedListeners.filter(l => l !== notify); };
}

export function subscribeFailedPressSyncs(
  roundId: string,
  fn: (presses: FailedPressInfo[]) => void,
): () => void {
  const notify = () => {
    const presses = loadFailed()
      .filter(e => e.action.type === 'press' && e.action.roundId === roundId)
      .map(e => {
        const a = e.action as Extract<SyncAction, { type: 'press' }>;
        return { clientId: a.clientId ?? e.id, betType: a.betType, startingHole: a.startingHole };
      });
    fn(presses);
  };
  failedListeners.push(notify);
  notify();
  return () => { failedListeners = failedListeners.filter(l => l !== notify); };
}

export function subscribeFailedDeleteSyncs(
  roundId: string,
  fn: (deletes: FailedDeleteInfo[]) => void,
): () => void {
  const notify = () => {
    const deletes = loadFailed()
      .filter(
        e => e.action.type === 'delete_press' && e.action.roundId === roundId,
      )
      .map(e => ({
        pressId: (e.action as Extract<SyncAction, { type: 'delete_press' }>).pressId,
      }));
    fn(deletes);
  };
  failedListeners.push(notify);
  notify();
  return () => { failedListeners = failedListeners.filter(l => l !== notify); };
}

/**
 * Re-queue a permanently-failed delete_press entry for a given round.
 * Restores the original payload so the deletion is retried without user
 * having to redo the action.
 */
export function requeueFailedDeleteSync(roundId: string, pressId: string): void {
  const failed = loadFailed();
  const match = failed.find(
    e => e.action.type === 'delete_press' && e.action.roundId === roundId &&
      (e.action as Extract<SyncAction, { type: 'delete_press' }>).pressId === pressId,
  );
  if (!match) return;
  const remaining = failed.filter(e => e !== match);
  saveFailed(remaining);
  enqueueSync(match.action, match.commissionerUserId);
}

/**
 * Live count of `press` actions still sitting in the *active* queue (not yet
 * failed) for a round — used to block finalize while a press sync is still
 * in flight/retrying, not just after it has permanently failed.
 */
export function subscribePendingPressCount(
  roundId: string,
  fn: (count: number) => void,
): () => void {
  const notify = (count: number) => {
    // count param comes from the generic queue-length listener; recompute
    // the round+type-specific count from the current queue snapshot.
    const n = loadQueue().filter(
      e => e.action.type === 'press' && e.action.roundId === roundId,
    ).length;
    fn(n);
  };
  listeners.push(notify);
  notify(0);
  return () => { listeners = listeners.filter(l => l !== notify); };
}

/**
 * Re-queue a permanently-failed manual press for a given round.
 * Restores the original payload so the user never has to redo the press.
 */
export function requeueFailedPressSync(roundId: string, clientId: string): void {
  const failed = loadFailed();
  const match = failed.find(
    e => e.action.type === 'press' && e.action.roundId === roundId &&
      (e.action.clientId ?? e.id) === clientId,
  );
  if (!match) return;
  const remaining = failed.filter(e => e !== match);
  saveFailed(remaining);
  enqueueSync(match.action, match.commissionerUserId);
}

/**
 * Re-queue a permanently-failed hole_scores entry for a given hole.
 * Restores the original payload so the user never has to re-type scores.
 * If connectivity has returned the retry will succeed immediately.
 */
export function requeueFailedHoleSync(roundId: string, holeNumber: number): void {
  const failed = loadFailed();
  const match = failed
    .filter(e => e.action.type === 'hole_scores' && e.action.roundId === roundId && (e.action as Extract<SyncAction, { type: 'hole_scores' }>).holeNumber === holeNumber)
    .at(-1); // most recent failed entry
  if (!match) return;
  const remaining = failed.filter(e => e !== match);
  saveFailed(remaining);
  // Re-enqueue with a fresh id and reset retries
  enqueueSync(match.action, match.commissionerUserId);
}

/** Remove all failed hole_scores entries for a round (e.g. on discard). */
export function clearFailedSyncsForRound(roundId: string): void {
  const remaining = loadFailed().filter(e => {
    if ('roundId' in e.action) return e.action.roundId !== roundId;
    return true;
  });
  saveFailed(remaining);
}

/** Remove failed entries for one specific hole (called when user re-enters that hole). */
export function clearFailedHoleSync(roundId: string, holeNumber: number): void {
  const remaining = loadFailed().filter(e => {
    if (e.action.type !== 'hole_scores') return true;
    if (e.action.roundId !== roundId) return true;
    return e.action.holeNumber !== holeNumber;
  });
  saveFailed(remaining);
}

function loadQueue(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveQueue(queue: QueueEntry[]): void {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    notifyListeners(queue.length);
  } catch (e) {
    console.error('[syncQueue] Failed to save queue:', e);
  }
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

async function executeAction(action: SyncAction): Promise<void> {
  // ── Dev-only test hooks ──────────────────────────────────────────────────────
  // Set window.__dpForceFailHoleScores / window.__dpForceFailPress = true in a
  // test to force that action type to throw without touching the network.
  // Set window.__dpTestForceAuthFalse = true to simulate 401 responses from all
  // sync requests (exercises the auth-hold path in processQueue).
  // Never compiled into production.
  if (import.meta.env.DEV) {
    if ((window as any).__dpTestForceAuthFalse === true) {
      const { ApiError: AE } = await import('@/lib/queryClient');
      throw new AE(401, `/${action.type}`, 'Unauthorized');
    }
    if (action.type === 'hole_scores' && (window as any).__dpForceFailHoleScores === true) {
      throw new Error('[syncQueue test hook] forced hole_scores failure');
    }
    if (action.type === 'press' && (window as any).__dpForceFailPress === true) {
      throw new Error('[syncQueue test hook] forced press failure');
    }
  }
  // ── End test hooks ───────────────────────────────────────────────────────────
  switch (action.type) {
    case 'hole_scores':
      await apiRequest('PUT', `/api/rounds/${action.roundId}/holes/${action.holeNumber}`, { scores: action.scores });
      break;
    case 'press':
      await apiRequest('POST', `/api/rounds/${action.roundId}/press`, {
        betType: action.betType,
        initiatedBy: action.initiatedBy,
        startingHole: action.startingHole,
        stackCount: action.stackCount,
        parentPressId: action.parentPressId,
        kind: 'manual',
        clientId: action.clientId,
      });
      break;
    case 'clear_holes':
      await apiRequest('DELETE', `/api/rounds/${action.roundId}/holes/clear`, { holeNumbers: action.holeNumbers });
      break;
    case 'delete_press':
      await apiRequest('DELETE', `/api/rounds/${action.roundId}/press/${action.pressId}`);
      break;
    case 'active_hole':
      await apiRequest('PUT', `/api/rounds/${action.roundId}/active-hole`, { holeNumber: action.holeNumber });
      break;
  }
}

// In dev, window.__dpTestSyncConfig lets tests override timing/retry behaviour:
//   { maxRetries: 1, backoffMs: 0, canonicalLookupDelayMs: 0 }
// Has no effect in production builds.
function getMaxRetries(): number {
  if (import.meta.env.DEV) {
    const cfg = (window as any).__dpTestSyncConfig;
    if (cfg && typeof cfg.maxRetries === 'number') return cfg.maxRetries;
  }
  return 5;
}
function getBackoffMs(retryCount: number): number {
  if (import.meta.env.DEV) {
    const cfg = (window as any).__dpTestSyncConfig;
    if (cfg && typeof cfg.backoffMs === 'number') return cfg.backoffMs;
  }
  return 2000 * Math.pow(2, retryCount - 1);
}
/** Bounded delay before the second canonical GET on a first-404. 0 in tests. */
function getCanonicalLookupDelayMs(): number {
  if (import.meta.env.DEV) {
    const cfg = (window as any).__dpTestSyncConfig;
    if (cfg && typeof cfg.canonicalLookupDelayMs === 'number') return cfg.canonicalLookupDelayMs;
  }
  return 2000;
}

// ── 404 verification helpers ─────────────────────────────────────────────────
// Used by processQueue when a queued mutation receives a 404. Instead of
// immediately classifying the round as missing (which would be wrong for a
// transient routing hiccup), we do a canonical GET to confirm the round's state.

type CanonicalVerifyStatus = 200 | 401 | 403 | 404 | 410 | 'transient';

/**
 * Calls GET /api/rounds/:roundId to determine whether a round truly exists.
 * Returns a typed status code so the caller can dispatch to the right recovery path.
 * Network failures and unexpected 5xx responses are bucketed as 'transient'.
 */
async function verifyRoundExists(roundId: string): Promise<CanonicalVerifyStatus> {
  try {
    await apiRequest('GET', `/api/rounds/${roundId}`);
    return 200;
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) return 401;
      if (err.status === 403) return 403;
      if (err.status === 404) return 404;
      if (err.status === 410) return 410;
    }
    return 'transient';
  }
}

/**
 * Moves all queued entries for `missingRoundId` into the round_missing store.
 * Called only after round absence has been double-confirmed via canonical GET.
 * Reads the latest queue from localStorage so in-flight enqueueSync calls
 * that arrived during async awaits are included.
 */
function classifyRoundMissing(missingRoundId: string): void {
  const latestQueue = loadQueue();
  const remaining: QueueEntry[] = [];
  const pulled: QueueEntry[] = [];
  for (const e of latestQueue) {
    if ((e.action as any).roundId === missingRoundId) pulled.push(e);
    else remaining.push(e);
  }
  saveQueue(remaining);
  const missing = loadRoundMissing();
  missing.push({ roundId: missingRoundId, entries: pulled, detectedAt: Date.now() });
  saveRoundMissing(missing);
  notifyRoundMissing(missingRoundId);
}
// ─────────────────────────────────────────────────────────────────────────────

let isProcessing = false;
let drainResolvers: Array<() => void> = [];

function resolveDrainers() {
  const resolvers = drainResolvers;
  drainResolvers = [];
  resolvers.forEach(r => r());
}

async function processQueue(currentUserId?: string): Promise<void> {
  if (isProcessing) return;
  isProcessing = true;

  try {
    while (true) {
      const queue = loadQueue();
      if (queue.length === 0) break;

      const entry = queue[0];

      // Commissioner identity guard — FAIL CLOSED.
      //
      // Legacy entries (commissionerUserId undefined) are always processed.
      //
      // For stamped entries three cases are evaluated explicitly:
      //
      //  currentUserId absent  → We do not know who is flushing. Do NOT execute.
      //                          Preserve the entry (no retries consumed). Enter
      //                          auth-hold so the app re-fetches the session and
      //                          re-fires flushQueuedSyncs once auth is restored.
      //
      //  currentUserId differs → A different account is signed in. Do NOT execute.
      //                          Preserve the entry. Surface commissioner mismatch.
      //
      //  currentUserId matches → Proceed to executeAction.
      if (entry.commissionerUserId) {
        if (!currentUserId) {
          console.warn('[syncQueue] Flush halted — currentUserId absent for a commissioner-stamped entry. Entering auth-hold.', {
            entryCommissioner: entry.commissionerUserId,
          });
          queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
          break;
        }
        if (entry.commissionerUserId !== currentUserId) {
          console.warn('[syncQueue] Flush halted — commissioner mismatch. Waiting for original commissioner to reconnect.', {
            entryCommissioner: entry.commissionerUserId,
            currentUser: currentUserId,
          });
          notifyOwnershipError('commissioner_mismatch');
          break;
        }
        // entry.commissionerUserId === currentUserId → proceed
      }

      try {
        await executeAction(entry.action);
        const freshQueue = loadQueue();
        const updated = freshQueue.filter(e => e.id !== entry.id);
        saveQueue(updated);
      } catch (err: unknown) {
        const freshQueue = loadQueue();
        const entryIdx = freshQueue.findIndex(e => e.id === entry.id);
        if (entryIdx < 0) continue;

        const apiErr = err instanceof ApiError ? err : null;

        if (apiErr?.isAuthError) {
          // 401: auth-hold — do NOT consume retries; exit so the flush trigger
          // can re-fire once isAuthenticated is restored (silent recovery).
          // The queue is preserved exactly as-is.
          console.warn('[syncQueue] 401 on queue action — entering auth-hold. Retry counters frozen.', {
            actionType: entry.action.type,
            endpoint: apiErr.endpoint,
          });
          // Trigger an immediate re-fetch of the auth state so the app can
          // surface the sign-in prompt or re-authenticate without waiting for
          // the 5-minute staleTime to expire or Clerk's isSignedIn to change.
          queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
          break; // exits while(true), reaches finally → resolveDrainers()
        }

        if (apiErr?.isOwnershipError) {
          // 403: do NOT increment retries; do NOT move to failedSyncs.
          // Inspect the body to surface the right message.
          console.warn('[syncQueue] 403 on queue action — ownership error.', {
            actionType: entry.action.type,
            body: apiErr.body,
          });
          notifyOwnershipError(apiErr.body);
          break; // pause the queue; Scorecard will surface the mismatch UI
        }

        if (apiErr?.isNotFound) {
          const missingRoundId = (entry.action as any).roundId as string;

          // 410 from the mutation itself: server explicitly signals permanent removal.
          // Classify as round_missing immediately — no canonical verification needed.
          if (apiErr.status === 410) {
            console.warn('[syncQueue] 410 on mutation — round permanently removed. Classifying as round_missing.', {
              roundId: missingRoundId,
            });
            classifyRoundMissing(missingRoundId);
            break;
          }

          // 404 from the mutation: do NOT classify immediately.
          // A transient routing issue or race condition could produce a spurious 404
          // even when the round still exists. Verify via canonical GET first.
          console.warn('[syncQueue] 404 on mutation — verifying via canonical GET before classifying.', {
            roundId: missingRoundId,
          });
          const firstVerify = await verifyRoundExists(missingRoundId);

          if (firstVerify === 200) {
            // Round confirmed to exist — treat mutation 404 as a transient error.
            // Increment retries so the entry eventually exhausts rather than
            // looping forever if the server keeps returning 404 for this mutation.
            const retryQueue = loadQueue();
            const retryIdx = retryQueue.findIndex(e => e.id === entry.id);
            if (retryIdx >= 0) {
              retryQueue[retryIdx].retries += 1;
              if (retryQueue[retryIdx].retries >= getMaxRetries()) {
                const [dropped] = retryQueue.splice(retryIdx, 1);
                saveQueue(retryQueue);
                if (dropped.action.type === 'hole_scores' || dropped.action.type === 'press' || dropped.action.type === 'delete_press') {
                  const failed = loadFailed();
                  failed.push(dropped);
                  saveFailed(failed);
                }
              } else {
                saveQueue(retryQueue);
                await new Promise(r => setTimeout(r, getBackoffMs(retryQueue[retryIdx].retries)));
              }
            }
            break;
          }

          if (firstVerify === 401) {
            // Auth-hold: preserve queue, trigger re-fetch.
            queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
            break;
          }

          if (firstVerify === 403) {
            // Ownership mismatch.
            notifyOwnershipError('commissioner_mismatch');
            break;
          }

          if (firstVerify === 410) {
            classifyRoundMissing(missingRoundId);
            break;
          }

          if (firstVerify === 404) {
            // First canonical GET also returned 404.
            // Wait a bounded delay then retry once before committing to round_missing.
            const delay = getCanonicalLookupDelayMs();
            if (delay > 0) await new Promise(r => setTimeout(r, delay));
            const secondVerify = await verifyRoundExists(missingRoundId);
            if (secondVerify === 404 || secondVerify === 410) {
              console.warn('[syncQueue] Second canonical GET confirmed round not found. Classifying as round_missing.', {
                roundId: missingRoundId,
              });
              classifyRoundMissing(missingRoundId);
            } else {
              // 200, 401, 403, or transient on second check — preserve queue conservatively.
              console.log('[syncQueue] Second canonical GET returned non-404. Preserving queue.', { secondVerify });
              if (secondVerify === 401) queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
              if (secondVerify === 403) notifyOwnershipError('commissioner_mismatch');
            }
            break;
          }

          // firstVerify === 'transient': network error or 5xx on canonical GET.
          // Treat as transient — preserve queue, do not enter round_missing.
          console.warn('[syncQueue] Canonical GET failed transiently after mutation 404. Preserving queue.', {
            roundId: missingRoundId,
          });
          break;
        }

        // Retryable errors (5xx, 408, 429) and unexpected errors:
        // increment retries and apply backoff.
        freshQueue[entryIdx].retries += 1;
        if (freshQueue[entryIdx].retries >= getMaxRetries()) {
          const [dropped] = freshQueue.splice(entryIdx, 1);
          saveQueue(freshQueue);
          if (dropped.action.type === 'hole_scores' || dropped.action.type === 'press' || dropped.action.type === 'delete_press') {
            // Preserve failed hole_scores, presses, AND delete_press actions so
            // the UI can surface them and the user can retry without redoing the
            // action. All three are money/state-affecting and must never vanish
            // silently.
            const failed = loadFailed();
            failed.push(dropped);
            saveFailed(failed);
          } else {
            console.error(`[syncQueue] Dropping action after ${getMaxRetries()} retries:`, dropped.action);
          }
        } else {
          saveQueue(freshQueue);
          await new Promise(r => setTimeout(r, getBackoffMs(freshQueue[entryIdx].retries)));
        }
      }
    }
  } finally {
    isProcessing = false;
    resolveDrainers();
  }
}

// ── Ownership error notification ──────────────────────────────────────────────
// Called when processQueue encounters a 403. Scorecard subscribes to this to
// show the commissioner-mismatch banner.
let ownershipErrorListeners: Array<(body: string) => void> = [];

export function subscribeOwnershipErrors(fn: (body: string) => void): () => void {
  ownershipErrorListeners.push(fn);
  return () => { ownershipErrorListeners = ownershipErrorListeners.filter(l => l !== fn); };
}

function notifyOwnershipError(body: string): void {
  ownershipErrorListeners.forEach(fn => fn(body));
}
// ─────────────────────────────────────────────────────────────────────────────

// ── Round-missing store ───────────────────────────────────────────────────────
// When a sync action for a round gets a 404/410, all queued entries for that
// round are moved here — preserved with full payload, never retried, and never
// confused with ordinary retry-exhaustion failures in the failed-sync store.
const ROUND_MISSING_KEY = 'doublepress_round_missing';

interface RoundMissingEntry {
  roundId: string;
  entries: QueueEntry[];
  detectedAt: number;
}

function loadRoundMissing(): RoundMissingEntry[] {
  try {
    return JSON.parse(localStorage.getItem(ROUND_MISSING_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveRoundMissing(data: RoundMissingEntry[]): void {
  try {
    localStorage.setItem(ROUND_MISSING_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('[syncQueue] Failed to save round_missing store:', e);
  }
}

/** Returns true if roundId is classified as round_missing (404/410 was received). */
export function isRoundMissing(roundId: string): boolean {
  return loadRoundMissing().some(m => m.roundId === roundId);
}

let roundMissingListeners: Array<(roundId: string) => void> = [];

/** Subscribe to round_missing events. Fired when processQueue receives 404/410. */
export function subscribeRoundMissing(fn: (roundId: string) => void): () => void {
  roundMissingListeners.push(fn);
  return () => { roundMissingListeners = roundMissingListeners.filter(l => l !== fn); };
}

function notifyRoundMissing(roundId: string): void {
  roundMissingListeners.forEach(fn => fn(roundId));
}
// ─────────────────────────────────────────────────────────────────────────────

export function enqueueSync(action: SyncAction, commissionerUserId?: string): void {
  let queue = loadQueue();

  if (action.type === 'active_hole') {
    const existingIdx = queue.findIndex(
      e => e.action.type === 'active_hole' && e.action.roundId === action.roundId
    );
    if (existingIdx >= 0) {
      queue[existingIdx].action = action;
      queue[existingIdx].retries = 0;
      saveQueue(queue);
      // Thread the commissioner identity so the fail-closed guard can evaluate
      // immediately without waiting for a flushQueuedSyncs call.
      processQueue(commissionerUserId);
      return;
    }
  }

  if (action.type === 'hole_scores') {
    // Clear any permanently-failed entry for this hole — a new enqueue means
    // the user has re-entered or retried, so the failure is superseded.
    clearFailedHoleSync(action.roundId, action.holeNumber);

    const existingIdx = queue.findIndex(
      e => e.action.type === 'hole_scores' && e.action.roundId === action.roundId && e.action.holeNumber === action.holeNumber
    );
    if (existingIdx >= 0) {
      queue[existingIdx].action = action;
      queue[existingIdx].retries = 0;
      saveQueue(queue);
      processQueue(commissionerUserId);
      return;
    }
  }

  // When deleting a press, cancel any pending `press` sync with the same clientId.
  // This prevents the zombie-resurrection race where a queued-but-not-yet-sent press
  // fires after the delete, re-creating the press on the server.
  if (action.type === 'delete_press') {
    const before = queue.length;
    queue = queue.filter(e => {
      if (e.action.type !== 'press') return true;
      if (e.action.roundId !== action.roundId) return true;
      return e.action.clientId !== action.pressId;
    });
    if (queue.length !== before) {
      console.log(`[syncQueue] Cancelled ${before - queue.length} pending press sync(s) for deleted press ${action.pressId}`);
    }
  }

  // When clearing holes, cancel any pending `press` syncs whose startingHole falls
  // in the cleared range. The server-side cascade will handle any already-created
  // presses; stale queued press syncs would resurrect them after the clear fires.
  if (action.type === 'clear_holes') {
    const holeSet = new Set(action.holeNumbers);
    const before = queue.length;
    queue = queue.filter(e => {
      if (e.action.type !== 'press') return true;
      if (e.action.roundId !== action.roundId) return true;
      return !holeSet.has(e.action.startingHole);
    });
    if (queue.length !== before) {
      console.log(`[syncQueue] Cancelled ${before - queue.length} pending press sync(s) for cleared holes ${action.holeNumbers.join(',')}`);
    }
  }

  queue.push({
    id: generateId(),
    action,
    retries: 0,
    createdAt: Date.now(),
    commissionerUserId,
  });
  saveQueue(queue);
  // Thread commissionerUserId so the fail-closed guard in processQueue can
  // authorise this immediate-execution attempt without requiring a separate flush.
  processQueue(commissionerUserId);
}

/**
 * Flush all queued sync entries to the server.
 *
 * `currentUserId` is a REQUIRED parameter (not optional) — callers must
 * explicitly pass the signed-in user's ID or `undefined`. Passing `undefined`
 * is accepted (it means "we know the user is absent") and will trigger
 * auth-hold for any commissioner-stamped entry, which is the correct behaviour.
 * Making this required prevents accidental omission that would silently produce
 * a fail-open execution.
 */
export async function flushQueuedSyncs(currentUserId: string | undefined): Promise<{ queuedRemaining: number }> {
  if (!isProcessing && loadQueue().length === 0) return { queuedRemaining: 0 };

  if (isProcessing) {
    await new Promise<void>(resolve => { drainResolvers.push(resolve); });
    return { queuedRemaining: loadQueue().length };
  }

  await processQueue(currentUserId);
  return { queuedRemaining: loadQueue().length };
}

export function getQueuedCount(): number {
  return loadQueue().length;
}

/**
 * Gate B: returns true if any entry in either the active queue OR the failed
 * queue belongs to the given round. Used by the recovery retry handler to
 * gate finalization — a round must not be finalized while it has unsynced or
 * failed actions, or the server and client may disagree on the final state.
 */
export function hasSyncIssuesForRound(roundId: string): boolean {
  const activeHasRound = loadQueue().some(e => (e.action as any).roundId === roundId);
  const failedHasRound = loadFailed().some(e => (e.action as any).roundId === roundId);
  return activeHasRound || failedHasRound;
}

export function clearQueueForRound(roundId: string): void {
  const queue = loadQueue().filter(e => {
    if ('roundId' in e.action) return e.action.roundId !== roundId;
    return true;
  });
  saveQueue(queue);
}

